import { CHANNEL_TYPES } from '@/config/sessionConstants'
import type { OrgSegment, TenantSegment } from '@/lib/routeScope'
import { z } from 'zod'

/**
 * Route parameter schemas shared across sections.
 *
 * The router is where a URL segment enters the app, so it is where the tenancy segments get branded:
 * every `params` a navigation builds is then required to hold segments rather than identifiers.
 */
export const tenantParamsSchema = z.object({
	tenantId: z
		.string()
		.min(1, 'Project ID is required')
		.transform(value => value as TenantSegment),
})

export const orgIdParamsSchema = z.object({
	orgId: z
		.string()
		.min(1, 'Organization ID is required')
		.transform(value => value as OrgSegment),
})

export const statusParamsSchema = z.object({
	status: z.enum(['active', 'inactive', 'all']),
})

/**
 * The two shapes a tab parameter takes, shared by the modules with one tab. `tabAllOnlySchema` rejects
 * all but `all`, so a bad segment 404s; `tabAnySchema` takes whatever the URL names, for the modules
 * whose tab is a project id or a rule name rather than a fixed word.
 */
export const tabAllOnlySchema = z.object({
	type: z.enum(['all']),
})

export const tabAnySchema = z.object({
	type: z.string().default('all'),
})

// Unlike tabAllOnlySchema, a stray segment here lands on the `all` tab instead of a 404.
export const apiTypeSchema = z.object({
	type: z.enum(['all']).catch('all'),
})

/** Subscriber group tabs: All plus one per notification channel, so any other segment 404s. */
export const subscriberGroupTypeSchema = z.object({
	type: z.enum(['all', ...Object.values(CHANNEL_TYPES)]),
})
